import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  Psychology as PsychologyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import api from '../utils/api';

// ---- AI Settings Section (standalone state, separate save) ----
const AISettingsSection = () => {
  const [aiSettings, setAiSettings] = useState({
    openai_api_key: '',
    google_api_key: '',
    ollama_base_url: 'http://host.docker.internal:11434',
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success: bool, message: string }
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showGoogleKey, setShowGoogleKey] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchAISettings = async () => {
      setLoading(true);
      try {
        const response = await api.get('/settings/ai');
        const data = response.data || {};
        setAiSettings({
          openai_api_key: data.openai_api_key || '',
          google_api_key: data.google_api_key || '',
          ollama_base_url: data.ollama_base_url || 'http://host.docker.internal:11434',
        });
        setOriginalSettings({
          openai_api_key: data.openai_api_key || '',
          google_api_key: data.google_api_key || '',
          ollama_base_url: data.ollama_base_url || 'http://host.docker.internal:11434',
        });
      } catch (error) {
        console.error('Error fetching AI settings:', error);
        setSnackbar({ open: true, message: 'Error loading AI settings', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchAISettings();
  }, []);

  const isMasked = (value) => value && value.includes('****');

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that actually changed.
      // If a key still shows its masked value, don't send it (unchanged).
      const payload = {};
      if (aiSettings.openai_api_key !== originalSettings.openai_api_key && !isMasked(aiSettings.openai_api_key)) {
        payload.openai_api_key = aiSettings.openai_api_key;
      }
      if (aiSettings.google_api_key !== originalSettings.google_api_key && !isMasked(aiSettings.google_api_key)) {
        payload.google_api_key = aiSettings.google_api_key;
      }
      if (aiSettings.ollama_base_url !== originalSettings.ollama_base_url) {
        payload.ollama_base_url = aiSettings.ollama_base_url;
      }

      await api.put('/settings/ai', payload);
      // Refetch to get the new masked values
      const response = await api.get('/settings/ai');
      const data = response.data || {};
      const updated = {
        openai_api_key: data.openai_api_key || '',
        google_api_key: data.google_api_key || '',
        ollama_base_url: data.ollama_base_url || 'http://host.docker.internal:11434',
      };
      setAiSettings(updated);
      setOriginalSettings(updated);
      setSnackbar({ open: true, message: 'AI settings saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving AI settings:', error);
      setSnackbar({ open: true, message: 'Error saving AI settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestOllama = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.get('/ollama/models', {
        params: aiSettings.ollama_base_url ? { base_url: aiSettings.ollama_base_url } : {},
      });
      const count = response.data?.models?.length || 0;
      setTestResult({
        success: true,
        message: `Connected successfully. ${count} model${count !== 1 ? 's' : ''} available.`,
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || error.message || 'Connection failed.',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClearKey = (field) => {
    setAiSettings((prev) => ({ ...prev, [field]: '' }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Typography variant="h6" gutterBottom>
          AI Provider Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure API keys and connection settings for AI providers. These settings are used by all AI modules across the platform.
        </Typography>

        {/* OpenAI API Key */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            OpenAI API Key
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              value={aiSettings.openai_api_key}
              onChange={(e) => setAiSettings((prev) => ({ ...prev, openai_api_key: e.target.value }))}
              type={showOpenAIKey ? 'text' : 'password'}
              fullWidth
              size="small"
              placeholder="sk-..."
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex' }}>
                    <IconButton
                      size="small"
                      onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                      edge="end"
                    >
                      {showOpenAIKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                    {aiSettings.openai_api_key && (
                      <IconButton
                        size="small"
                        onClick={() => handleClearKey('openai_api_key')}
                        edge="end"
                        title="Clear"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ),
              }}
            />
          </Box>
          {isMasked(aiSettings.openai_api_key) && (
            <Typography variant="caption" color="text.secondary">
              Key is set. Enter a new value to replace it, or clear to remove.
            </Typography>
          )}
        </Box>

        {/* Google API Key */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Google API Key
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              value={aiSettings.google_api_key}
              onChange={(e) => setAiSettings((prev) => ({ ...prev, google_api_key: e.target.value }))}
              type={showGoogleKey ? 'text' : 'password'}
              fullWidth
              size="small"
              placeholder="AIza..."
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex' }}>
                    <IconButton
                      size="small"
                      onClick={() => setShowGoogleKey(!showGoogleKey)}
                      edge="end"
                    >
                      {showGoogleKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                    {aiSettings.google_api_key && (
                      <IconButton
                        size="small"
                        onClick={() => handleClearKey('google_api_key')}
                        edge="end"
                        title="Clear"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ),
              }}
            />
          </Box>
          {isMasked(aiSettings.google_api_key) && (
            <Typography variant="caption" color="text.secondary">
              Key is set. Enter a new value to replace it, or clear to remove.
            </Typography>
          )}
        </Box>

        {/* Ollama Base URL */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Ollama Base URL
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              value={aiSettings.ollama_base_url}
              onChange={(e) => setAiSettings((prev) => ({ ...prev, ollama_base_url: e.target.value }))}
              fullWidth
              size="small"
              placeholder="http://host.docker.internal:11434"
            />
            <Button
              variant="outlined"
              onClick={handleTestOllama}
              disabled={testing}
              startIcon={testing ? <CircularProgress size={16} /> : null}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
          </Box>
          {testResult && (
            <Alert
              severity={testResult.success ? 'success' : 'error'}
              icon={testResult.success ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
              sx={{ mt: 1 }}
              onClose={() => setTestResult(null)}
            >
              {testResult.message}
            </Alert>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save AI Settings'}
        </Button>
      </Grid>

      <Grid item xs={12} md={5}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PsychologyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">
                AI Providers
              </Typography>
            </Box>
            <Typography variant="body2" paragraph>
              API keys configured here are used globally by all AI enrichment modules, pipeline generation, and Knowledge Base chat features.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>OpenAI:</strong> Required for GPT models. Get your API key from{' '}
              <code>platform.openai.com</code>.
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Google:</strong> Required for Gemini models. Get your API key from{' '}
              <code>aistudio.google.com</code>.
            </Typography>
            <Typography variant="body2">
              <strong>Ollama:</strong> Free, locally-hosted models. No API key needed -- just ensure Ollama is running and accessible at the configured URL.
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SettingsPage = () => {
  // States
  const [settings, setSettings] = useState({
    general: {
      site_name: 'Data Enrichment Platform',
      support_email: '',
      enable_telemetry: true,
    },
    security: {
      password_expiry_days: 90,
      session_timeout_minutes: 30,
      allow_multiple_sessions: true,
      require_2fa: false,
    },
    notifications: {
      email_notifications: true,
      slack_webhook_url: '',
      notify_on_pipeline_failure: true,
      notify_on_pipeline_success: false,
      daily_summary_enabled: true,
    },
    storage: {
      temp_data_retention_days: 7,
      run_logs_retention_days: 30,
      max_file_upload_size_mb: 10,
      default_storage_path: '/tmp/dep-data',
    },
    api: {
      rate_limit_per_minute: 60,
      timeout_seconds: 30,
      default_page_size: 100,
      max_page_size: 1000,
    },
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/settings/');
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
        setSnackbar({
          open: true,
          message: 'Error loading settings',
          severity: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle setting change
  const handleSettingChange = (section, key, value) => {
    setSettings({
      ...settings,
      [section]: {
        ...settings[section],
        [key]: value,
      },
    });
  };
  
  // Save settings
  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await api.put('/settings/', settings);
      
      setSnackbar({
        open: true,
        message: 'Settings saved successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving settings',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Settings</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          disabled={isSaving}
        >
          {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </Box>
      
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="General" />
          <Tab label="AI Configuration" />
          <Tab label="Security" />
          <Tab label="Notifications" />
          <Tab label="Storage" />
          <Tab label="API Settings" />
        </Tabs>
        
        <Divider />
        
        {/* General Settings */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Site Name"
                value={settings.general.site_name}
                onChange={(e) => handleSettingChange('general', 'site_name', e.target.value)}
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Support Email"
                value={settings.general.support_email}
                onChange={(e) => handleSettingChange('general', 'support_email', e.target.value)}
                fullWidth
                margin="normal"
                type="email"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.general.enable_telemetry}
                    onChange={(e) => handleSettingChange('general', 'enable_telemetry', e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Telemetry Data"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    About
                  </Typography>
                  <Typography variant="body2" paragraph>
                    Data Enrichment Platform is a modular platform for data integration, ETL/ELT processing, 
                    AI-powered enrichment, and multi-target delivery.
                  </Typography>
                  <Typography variant="body2">
                    Version: 0.1.0
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* AI Configuration */}
        <TabPanel value={activeTab} index={1}>
          <AISettingsSection />
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Password Expiry (Days)"
                value={settings.security.password_expiry_days}
                onChange={(e) => handleSettingChange('security', 'password_expiry_days', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Session Timeout (Minutes)"
                value={settings.security.session_timeout_minutes}
                onChange={(e) => handleSettingChange('security', 'session_timeout_minutes', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.allow_multiple_sessions}
                    onChange={(e) => handleSettingChange('security', 'allow_multiple_sessions', e.target.checked)}
                    color="primary"
                  />
                }
                label="Allow Multiple Sessions"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.require_2fa}
                    onChange={(e) => handleSettingChange('security', 'require_2fa', e.target.checked)}
                    color="primary"
                  />
                }
                label="Require Two-Factor Authentication"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Security Recommendations
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    • Enable two-factor authentication for enhanced security
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Set reasonable session timeouts to prevent unauthorized access
                  </Typography>
                  <Typography variant="body2">
                    • Regularly rotate API keys and credentials
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Notification Settings */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.email_notifications}
                    onChange={(e) => handleSettingChange('notifications', 'email_notifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Email Notifications"
              />
              
              <TextField
                label="Slack Webhook URL"
                value={settings.notifications.slack_webhook_url}
                onChange={(e) => handleSettingChange('notifications', 'slack_webhook_url', e.target.value)}
                fullWidth
                margin="normal"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.notify_on_pipeline_failure}
                    onChange={(e) => handleSettingChange('notifications', 'notify_on_pipeline_failure', e.target.checked)}
                    color="primary"
                  />
                }
                label="Notify on Pipeline Failure"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.notify_on_pipeline_success}
                    onChange={(e) => handleSettingChange('notifications', 'notify_on_pipeline_success', e.target.checked)}
                    color="primary"
                  />
                }
                label="Notify on Pipeline Success"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.daily_summary_enabled}
                    onChange={(e) => handleSettingChange('notifications', 'daily_summary_enabled', e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Daily Summary"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <NotificationsIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Notification Channels
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Configure notifications to keep your team informed about pipeline status, errors, and system events.
                  </Typography>
                  <Typography variant="body2">
                    For Slack integration, you'll need to create an incoming webhook in your Slack workspace settings.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Storage Settings */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Temporary Data Retention (Days)"
                value={settings.storage.temp_data_retention_days}
                onChange={(e) => handleSettingChange('storage', 'temp_data_retention_days', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Run Logs Retention (Days)"
                value={settings.storage.run_logs_retention_days}
                onChange={(e) => handleSettingChange('storage', 'run_logs_retention_days', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Max File Upload Size (MB)"
                value={settings.storage.max_file_upload_size_mb}
                onChange={(e) => handleSettingChange('storage', 'max_file_upload_size_mb', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Default Storage Path"
                value={settings.storage.default_storage_path}
                onChange={(e) => handleSettingChange('storage', 'default_storage_path', e.target.value)}
                fullWidth
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <StorageIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Storage Management
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Set appropriate retention policies to manage disk space usage while maintaining necessary historical data.
                  </Typography>
                  <Typography variant="body2">
                    For production environments, consider using a distributed storage solution for improved scalability and resilience.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* API Settings */}
        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Rate Limit (Requests per Minute)"
                value={settings.api.rate_limit_per_minute}
                onChange={(e) => handleSettingChange('api', 'rate_limit_per_minute', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Request Timeout (Seconds)"
                value={settings.api.timeout_seconds}
                onChange={(e) => handleSettingChange('api', 'timeout_seconds', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Default Page Size"
                value={settings.api.default_page_size}
                onChange={(e) => handleSettingChange('api', 'default_page_size', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
              
              <TextField
                label="Maximum Page Size"
                value={settings.api.max_page_size}
                onChange={(e) => handleSettingChange('api', 'max_page_size', parseInt(e.target.value, 10))}
                type="number"
                fullWidth
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CodeIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      API Configuration
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Configure API settings to balance performance and resource usage. Higher rate limits may impact system performance.
                  </Typography>
                  <Typography variant="body2">
                    API documentation is available at <code>/api/docs</code>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;
